// 📄 pages/Proyectos/components/RegistroHorasCard.tsx
import React, { useState } from 'react';
import { Clock, Pencil, Trash2, CheckCircle2, RotateCcw, Users as UsersIcon, ShieldCheck } from 'lucide-react';
import { RegistroHoras, deleteRegistro, marcarFacturado, reabrirRegistro, reabrirAprobacion } from '../../../services/registroHorasService.ts';
import { ESTADO_FACTURACION_STYLES, ESTADO_APROBACION_STYLES } from './horasStyles.ts';

interface RegistroHorasCardProps {
  projectId: string;
  registro: RegistroHoras;
  solucionLabel: string;
  onEdit: () => void;
  onChanged: () => void;
}

export default function RegistroHorasCard({ projectId, registro, solucionLabel, onEdit, onChanged }: RegistroHorasCardProps) {
  const [working, setWorking] = useState(false);
  const fecha = registro.fecha?.toDate?.();

  // Dos bloqueos independientes: facturación (el consultor lo marcó
  // facturado) y aprobación (el cliente lo aceptó). Un registro puede
  // estar en ambos a la vez — cada uno se reabre por separado, sin que
  // reabrir uno afecte al otro.
  const bloqueadoPorFacturacion = registro.estadoFacturacion === 'Facturado';
  const bloqueadoPorAprobacion = registro.estadoAprobacion === 'Aceptado';
  const puedeEditar = !bloqueadoPorFacturacion && !bloqueadoPorAprobacion;

  const handleMarcarFacturado = async () => {
    setWorking(true);
    try {
      await marcarFacturado(projectId, registro.id);
      onChanged();
    } catch (err) {
      console.error('Error marcando registro como facturado:', err);
    } finally {
      setWorking(false);
    }
  };

  const handleReabrirFacturacion = async () => {
    setWorking(true);
    try {
      await reabrirRegistro(projectId, registro.id);
      onChanged();
    } catch (err) {
      console.error('Error reabriendo facturación:', err);
    } finally {
      setWorking(false);
    }
  };

  const handleReabrirAprobacion = async () => {
    setWorking(true);
    try {
      await reabrirAprobacion(projectId, registro.id);
      onChanged();
    } catch (err) {
      console.error('Error reabriendo aprobación:', err);
    } finally {
      setWorking(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar este registro de horas? Esta acción no se puede deshacer.')) return;
    setWorking(true);
    try {
      await deleteRegistro(projectId, registro.id);
      onChanged();
    } catch (err: any) {
      console.error('Error eliminando registro:', err);
      alert(err.message || 'Error al eliminar el registro.');
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className={`flex items-start gap-4 bg-white/5 border rounded-2xl p-4 transition-all duration-150 hover:border-blue-400/50 ${
      registro.estadoAprobacion === 'Objetado' ? 'border-red-500/40' : 'border-white/10'
    }`}>
      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-[rgba(91,155,240,0.12)]">
        <Clock className="w-5 h-5 text-blue-400" />
      </div>

      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-white">
            {fecha ? fecha.toLocaleDateString() : '—'}
          </span>
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${ESTADO_FACTURACION_STYLES[registro.estadoFacturacion]}`}>
            {registro.estadoFacturacion}
          </span>
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${ESTADO_APROBACION_STYLES[registro.estadoAprobacion]}`}>
            Cliente: {registro.estadoAprobacion}
          </span>
          {registro.huboSesion && (
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-indigo-500/10 text-indigo-400 border-indigo-500/30 flex items-center gap-1">
              <UsersIcon className="w-3 h-3" /> Con sesión
            </span>
          )}
        </div>

        <div className="flex items-center gap-3 flex-wrap text-xs text-gray-400">
          <span>{solucionLabel}</span>
          <span className="text-gray-600">&middot;</span>
          <span>{registro.horas.toLocaleString('es', { maximumFractionDigits: 2 })} h</span>
          <span className="text-gray-600">&middot;</span>
          <span className="text-white font-medium">${registro.totalCalculado.toLocaleString('es', { maximumFractionDigits: 2 })}</span>
        </div>

        {registro.cambiosRealizados && (
          <p className="text-xs text-gray-500 line-clamp-2">{registro.cambiosRealizados}</p>
        )}

        {registro.estadoAprobacion === 'Objetado' && registro.comentarioCliente && (
          <p className="text-xs text-red-400">Objeción del cliente: {registro.comentarioCliente}</p>
        )}
      </div>

      <div className="flex items-center gap-1 flex-shrink-0">
        {puedeEditar && (
          <>
            <button
              onClick={onEdit}
              disabled={working}
              title="Editar"
              className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-all disabled:opacity-40"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              onClick={handleDelete}
              disabled={working}
              title="Eliminar"
              className="p-2 rounded-lg text-gray-400 hover:text-red-400 hover:bg-white/10 transition-all disabled:opacity-40"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              onClick={handleMarcarFacturado}
              disabled={working}
              title="Marcar como facturado"
              className="p-2 rounded-lg text-gray-400 hover:text-emerald-400 hover:bg-white/10 transition-all disabled:opacity-40"
            >
              <CheckCircle2 className="w-4 h-4" />
            </button>
          </>
        )}
        {bloqueadoPorFacturacion && (
          <button
            onClick={handleReabrirFacturacion}
            disabled={working}
            title="Reabrir facturación (volver a Pendiente)"
            className="p-2 rounded-lg text-gray-400 hover:text-yellow-400 hover:bg-white/10 transition-all disabled:opacity-40"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        )}
        {bloqueadoPorAprobacion && (
          <button
            onClick={handleReabrirAprobacion}
            disabled={working}
            title="Reabrir aprobación (el cliente ya lo había aceptado)"
            className="p-2 rounded-lg text-gray-400 hover:text-indigo-400 hover:bg-white/10 transition-all disabled:opacity-40"
          >
            <ShieldCheck className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
